/* eslint-disable */
// ============================================================
// Portal navigation registry — Brief E.2 Phase 1
// ============================================================
// Single source of truth for portal-nav (AppHeader.js) AND the
// hamburger drawer (CatalogDrawer.js). Closes the deferred
// PORTAL_REGISTRY extraction from Brief F.
//
// Shape per portal:
//   key:         portal id (catalog | developments | qc | sales | orders | projects)
//   label:       human-visible name (e.g. "Originals")
//   soon:        true if not yet shipped (Orders / Projects)
//   subsections: array of sub-views, each with:
//     key:       sub-view id
//     label:     human-visible name
//     tab:       which `useUiStore.tab` value to set
//     devView:   optional — for developments sub-views, the `devView` slice
//
// Active-portal mapping (from `tab` to portal key) is computed in
// the consumer (AppHeader.js portalFromTab + isPortalSubActive).
//
// Brief G Phase 3b — extended to be single source of truth for both
// AppHeader (header nav) AND CatalogDrawer.DrawerPortalsList (hamburger
// drawer). DOCKET was the one drawer-only portal (`headerHidden: true`,
// trail "Tasks"); it was retired 2026-10-12 (CLB-074) and its screen lives
// in src/_archive/docket/. Both optional fields below still work.
//
// Optional per-portal fields:
//   trail?:        string — display a label pill instead of (or alongside)
//                  a chev. No portal uses it today.
//                  Consumed by DrawerPortalsList.
//   headerHidden?: boolean — exclude from AppHeader nav rendering. The
//                  header filters with `.filter(p => !p.headerHidden)`.
//                  No portal sets it today.
// ============================================================

export const PORTAL_NAV = [
  {
    key: 'catalog',
    label: 'Originals',
    subsections: [
      { key: 'items',        label: 'Items',        tab: 'catalog' },
      { key: 'overview',     label: 'Overview',     tab: 'overview' },
      { key: 'favorites',    label: 'Marked',       tab: 'favorites' },
      { key: 'dev_requests', label: 'Dev Requests', tab: 'dev_requests' },
    ],
  },
  {
    key: 'developments',
    label: 'Developments',
    subsections: [
      { key: 'board',      label: 'Board',       tab: 'developments', devView: 'board' },
      { key: 'list',       label: 'List',        tab: 'developments', devView: 'list' },
      { key: 'byDesigner', label: 'By Designer', tab: 'developments', devView: 'byDesigner' },
      { key: 'analytics',  label: 'Analytics',   tab: 'developments', devView: 'analytics' },
      { key: 'archive',    label: 'Archive',     tab: 'developments', devView: 'archive' },
      { key: 'settings',   label: 'Settings',    tab: 'developments', devView: 'settings' },
      { key: 'mailIntel',  label: 'Mail Intelligence', tab: 'developments', devView: 'mailIntel', miGated: true },
    ],
  },
  {
    key: 'qc',
    label: 'QC',
    // QC sub-views are owned by the QC portal's internal tab state; clicking
    // any QC sub here just navigates to the portal entry (`tab: 'qc'`). The
    // labels remain visible in the dropdown so the navigation surface mirrors
    // the prototype; deeper sub-tab routing is a follow-up.
    subsections: [
      { key: 'dashboard',     label: 'Dashboard',      tab: 'qc' },
      { key: 'reports',       label: 'Reports',        tab: 'qc' },
      { key: 'managerReview', label: 'Manager Review', tab: 'qc' },
      { key: 'analytics',     label: 'Analytics',      tab: 'qc' },
      { key: 'settings',      label: 'Settings',       tab: 'qc' },
    ],
  },
  {
    key: 'sales',
    label: 'Sales',
    // Sales fix pass FIX 1 — the legacy sub-tabs row is removed; these Sections
    // are now the ONLY way to switch Sales views. Each carries `salesView` which
    // AppHeader.handleSubsectionClick writes to uiStore (consumed by SalesPortal).
    subsections: [
      // Dashboard is deliberately FIRST — it is the portal's landing summary.
      // `featured` is an additive presentation flag consumed by SalesNavDropdown
      // (amber tint + grid icon + NEW pill); every other consumer ignores it.
      { key: 'dashboard', label: 'Dashboard',      tab: 'sales_portal', salesView: 'dashboard', featured: true, badge: 'NEW' },
      // `landing` keeps clicking the Sales TAB on Items Sales even though
      // Dashboard now sits first in the menu (brief: "default Sales landing stays
      // as-is"). Portals without a landing marker fall back to subsections[0].
      { key: 'items',     label: 'Items Sales',    tab: 'sales_portal', salesView: 'items', landing: true },
      { key: 'models',    label: 'Model Analysis', tab: 'sales_portal', salesView: 'models' },
      { key: 'analytics', label: 'Analytics',      tab: 'sales_portal', salesView: 'analytics' },
      { key: 'inventory', label: 'Inventory',      tab: 'sales_portal', salesView: 'inventory' },
      { key: 'data',      label: 'Data',           tab: 'sales_portal', salesView: 'data' },
      { key: 'events',    label: 'Event Journal',  tab: 'sales_portal', salesView: 'events' },
    ],
  },
  // Orders Phase 1 — activated 2026-07-25 (was a `soon` placeholder). No
  // `subsections`: the portal's four sections (Summary · Builder · Data ·
  // Insights) are driven by its own in-portal pill row, exactly as the approved
  // prototype does, with state in src/portals/orders/ordersUiStore.js. Clicking
  // the tab opens the portal on its default section.
  { key: 'orders', label: 'Orders' },
  {
    key: 'projects',
    label: 'Projects',
    // `projectsView` selects the Projects sub-tool (mirrors `devView`). The
    // AppHeader subsection-click handler sets it; handlePortalSwitch resets to
    // 'selection'. First entry is the default landed-on view (top-level click).
    subsections: [
      { key: 'selection',     label: 'Selection',      tab: 'projects', projectsView: 'selection', richLabel: 'inout-selection' },
      { key: 'sellsheets',    label: 'Sell Sheets',    tab: 'projects', projectsView: 'sellsheets' },
      { key: 'decisionemails', label: 'Decision Emails', tab: 'projects', projectsView: 'decisionemails' },
      // Window 4 (09.10): SS 2027 new models ordered vs Israel's request. `homeIcon` /
      // `homeDescription` are read by the Home shortcut card (homeDestinations.js).
      { key: 'balance', label: 'Ordered vs Asked', tab: 'projects', projectsView: 'balance', badge: 'NEW',
        homeIcon: 'balance', homeDescription: 'SS 2027 · new models vs IL request' },
    ],
  },
  // MAIL-1 (26.10) — Mail Intelligence as its own portal, super admin only. No
  // `subsections`: its six tabs are the portal's own sub header. Every consumer
  // gates it explicitly with useCanSeeMailPortal (src/portals/mail/mailGate.js);
  // it is deliberately absent from acConstants.js, so it can never be granted.
  { key: 'mail', label: 'Mail' },
];

// Map a `tab` value to its owning portal key. Used by AppHeader to
// determine which portal-nav item gets the active underline.
export function portalKeyFromTab(tab) {
  if (['catalog', 'dashboard', 'analytics', 'favorites', 'my_profile', 'users', 'settings', 'dev_requests', 'sales'].includes(tab)) {
    return 'catalog';
  }
  if (tab === 'developments') return 'developments';
  if (tab === 'qc') return 'qc';
  if (tab === 'sales_portal') return 'sales';
  if (tab === 'projects') return 'projects';
  if (tab === 'orders') return 'orders';
  if (tab === 'mail') return 'mail';
  return null;
}

// The sub-view a portal opens on when its TAB is clicked. Defaults to the first
// entry, unless one is explicitly marked `landing` — which lets a portal show a
// NEW section first in the menu without moving where the tab itself lands
// (Sales: Dashboard leads the menu, Items Sales stays the landing view).
export const defaultSub = (portal) =>
  (portal.subsections || []).find((x) => x.landing) || (portal.subsections || [])[0];
